/**
 * The one socket every screen holds open: `GET /api/v1/stream`, as server-sent events.
 *
 * A native `EventSource` does most of the work — it reconnects by itself after a dropped connection
 * and sends `Last-Event-ID`, so the server can replay what was missed. Three things it does not do,
 * and this file does:
 *
 *  - **It gives up quietly on an HTTP error.** A reconnect answered 401, 500, or a proxy's 502 while
 *    the API restarts, fires `error` once with the source CLOSED, and nothing retries ever again —
 *    and the source cannot say which status it got. So a closed source is followed by a `fetch` of
 *    the same URL to learn the status. A 401 is a decision about the credential and goes to
 *    `onUnauthorized` (the office goes to login, a TV back to pairing); anything else is retried
 *    with backoff, with a new source that resumes from the last event seen.
 *  - **Replay can repeat itself.** Event ids are handed out at insert and become visible at commit,
 *    so they can arrive out of order, and a reconnect can replay one this client already has. Events
 *    are deduped by id here, so nothing downstream ever sees the same event twice.
 *  - **A fresh source has no position.** Only the browser's own reconnects send `Last-Event-ID`; a
 *    source created after giving up passes the last id as `lastEventId` in the URL instead. The
 *    server lets the header win, so the browser's later reconnects still resume from where they are.
 *
 * `stock` and `resync` messages carry no id — they are not positions in the event log — and pass
 * straight through.
 *
 * And a fourth: **a connection can die without saying so** — a proxy that stops forwarding, a phone's
 * radio asleep — and the browser, hearing no error, waits forever (blind count I-1). The server's
 * heartbeat is a named `ping` event, which, unlike a comment, reaches the page, carrying its interval;
 * a stream that hears nothing at all for longer than that, plus a margin, is treated as dropped, and a
 * new source is opened. Pings are this file's own and are not passed on.
 */
export type StreamStatus = 'connecting' | 'live' | 'reconnecting';

/** Every message kind the server sends (`streamKind` in @pl/domain, plus the two it makes itself). */
const KINDS = ['box', 'order', 'scan_rejected', 'label', 'pallet', 'import', 'transfer', 'count', 'discrepancy', 'other', 'stock', 'resync'] as const;

/** `EventSource.CLOSED`, spelled out: a stand-in implementation need not carry the constants. */
const CLOSED = 2;
/** First wait before a new source after the browser gave up; doubled each time, up to the cap. */
export const RETRY_BASE_MS = 1000;
export const RETRY_MAX_MS = 15_000;
/** How many event ids to remember for dedupe. A replay is at most 500 events. */
export const SEEN_MAX = 2000;
/** The margin past a ping's interval before silence counts as a drop: half the interval, at most this. */
export const PING_GRACE_MS = 5_000;
/** How long a stream may hear nothing before it counts as dropped: well inside one heartbeat plus 10 s. */
export const silentAfter = (every: number) => every + Math.min(PING_GRACE_MS, every / 2);
/** The server's heartbeat until a ping says otherwise (`HEARTBEAT_MS` in apps/api's realtime routes). */
export const HEARTBEAT_MS = 25_000;
let lastHeard: number | null = null;
/** The heartbeat the page last heard from any stream: what a time away is measured against (review m-1). */
export const heartbeatMs = (): number => lastHeard ?? HEARTBEAT_MS;

export interface StreamOptions {
  /** A site id, or `all`. */
  site: string;
  /**
   * `scan` for the phone: the server then sends it no `stock` rows, which would otherwise put the
   * figures of a pair being counted blind on the wire. Every event still arrives.
   */
  surface?: 'scan';
  onMessage(kind: string, data: unknown): void;
  onStatus(s: StreamStatus): void;
  /** The server refused the credential (401). Retrying soon would only be refused again. */
  onUnauthorized?(): void;
  /**
   * Where to resume from on the first connection, e.g. the position a snapshot was taken at, so
   * nothing that commits between the snapshot and the subscription is lost.
   */
  lastEventId?: number;
  EventSourceImpl?: typeof EventSource;
  fetchImpl?: typeof fetch;
}

export function openStream(opts: StreamOptions): () => void {
  const Impl = opts.EventSourceImpl ?? (globalThis as { EventSource?: typeof EventSource }).EventSource;
  // A browser this old gets no live updates rather than a crash; its queries still poll.
  if (!Impl) return () => undefined;

  const base = `/api/v1/stream?siteId=${encodeURIComponent(opts.site)}${opts.surface ? `&surface=${opts.surface}` : ''}`;
  let es: EventSource | null = null;
  let closed = false;
  let retry: ReturnType<typeof setTimeout> | null = null;
  let probe: AbortController | null = null;
  let attempts = 0;
  /**
   * The server's heartbeat interval, from a ping on the current connection; null until one arrives.
   * Each connection starts without one (review m-2): a server rolled back past pings sends none, and
   * a page still running this code must not then drop its stream every heartbeat.
   */
  let every: number | null = null;
  let watchdog: ReturnType<typeof setTimeout> | null = null;
  /** The id of the last event received, as the browser itself would resend it. */
  let lastId: number | null = opts.lastEventId ?? null;
  const seen = new Set<number>();

  const unwatch = () => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = null;
  };
  /** Something arrived on the current source: it is alive for another heartbeat and a margin. */
  const heard = () => {
    unwatch();
    if (every === null || closed) return;
    watchdog = setTimeout(silent, silentAfter(every));
  };
  /** Nothing, not even a ping, for too long: the connection is gone without an error. */
  const silent = () => {
    watchdog = null;
    if (closed || !es) return;
    const source = es;
    es = null;
    source.close();
    opts.onStatus('reconnecting');
    scheduleReconnect(backoff());
  };

  const deliver = (kind: string, raw: string) => {
    heard();
    let data: unknown;
    try {
      data = JSON.parse(raw);
    } catch {
      return; // not ours to guess at
    }
    if (kind === 'ping') {
      const ms = (data as { every?: unknown } | null)?.every;
      if (typeof ms === 'number' && ms >= 100 && ms <= 600_000) {
        every = ms;
        lastHeard = ms;
        heard();
      }
      return;
    }
    const id = (data as { id?: unknown } | null)?.id;
    if (kind !== 'stock' && kind !== 'resync' && typeof id === 'number') {
      lastId = id;
      if (seen.has(id)) return;
      seen.add(id);
      // A Set iterates in insertion order, so the first entry is the oldest.
      if (seen.size > SEEN_MAX) seen.delete(seen.values().next().value!);
    }
    opts.onMessage(kind, data);
  };

  const connect = () => {
    const source = new Impl(lastId === null ? base : `${base}&lastEventId=${lastId}`, { withCredentials: true });
    es = source;
    source.onopen = () => {
      attempts = 0;
      // A new connection, the browser's own reconnect included: watched once it pings. The server
      // pings the moment a stream opens, so nothing is lost by waiting for it.
      every = null;
      heard();
      opts.onStatus('live');
    };
    source.onerror = () => {
      if (closed || es !== source) return;
      unwatch();
      opts.onStatus('reconnecting');
      // Still CONNECTING: the browser is retrying by itself. CLOSED: it never will.
      if (source.readyState === CLOSED) void diagnose(source);
    };
    for (const kind of [...KINDS, 'ping'] as const) source.addEventListener(kind, (e) => deliver(kind, (e as MessageEvent<string>).data));
  };

  const scheduleReconnect = (ms: number) => {
    if (closed) return;
    // `close()` clears this timer, so it never fires after the stream is closed.
    retry = setTimeout(() => {
      retry = null;
      connect();
    }, ms);
  };

  const backoff = () => {
    const ms = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** attempts);
    attempts++;
    return Math.round(ms * (0.8 + 0.4 * Math.random()));
  };

  /** Asks the same URL what the source could not say, then decides what to do about it. */
  const diagnose = async (source: EventSource) => {
    source.close();
    es = null;
    const fetchImpl = opts.fetchImpl ?? globalThis.fetch;
    // Chrome before 66 has no AbortController. The probe then goes without a signal, and a stream
    // that answers is let go by cancelling its body instead.
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    probe = ctrl;
    let status: number | null = null;
    try {
      const res = await fetchImpl(base, { credentials: 'same-origin', cache: 'no-store', headers: { accept: 'text/event-stream' }, signal: ctrl?.signal });
      status = res.status;
      if (!ctrl) void res.body?.cancel().catch(() => undefined);
    } catch {
      status = null; // no answer at all: the server is down or the network is
    } finally {
      // A 200 is a live stream of its own; only the status was wanted.
      ctrl?.abort();
      if (probe === ctrl) probe = null;
    }
    if (closed) return;
    if (status === 401) {
      opts.onUnauthorized?.();
      // Normally the screen goes away. If it does not, try again later rather than never.
      scheduleReconnect(RETRY_MAX_MS);
      return;
    }
    scheduleReconnect(backoff());
  };

  opts.onStatus('connecting');
  connect();

  return () => {
    closed = true;
    unwatch();
    if (retry) clearTimeout(retry);
    retry = null;
    probe?.abort();
    es?.close();
    es = null;
  };
}
